/**
 * Create a report, over the list rather than on its own page — the user keeps
 * their place in the board.
 *
 * Attaching media has two distinct workflows and the copy has to reflect both:
 *
 * - **Screenshots:** Ctrl+V is primary, not a hidden alternative. The Snipping
 *   Tool puts the bitmap straight on the clipboard, so capture-then-paste is the
 *   whole flow.
 * - **Screen recordings:** no recorder puts video on the clipboard — Xbox Game
 *   Bar and the Snipping Tool's recorder both write a file into Videos/Captures.
 *   So drag-drop and the file picker are the real paths for video. Paste still
 *   works if the user copies the file in Explorer first, which is why the paste
 *   handler accepts video too, but it is not the route worth advertising.
 */

import { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "sonner";

import { AttachmentTray } from "@/components/kehitys/AttachmentTray";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { ImageIcon } from "@/lib/icons";
import { FEEDBACK_KIND_META } from "@/lib/feedbackMeta";
import { cn } from "@/lib/utils";
import {
  useFeedbackAttachmentUpload,
  type UploadedAttachment,
} from "@/hooks/useFeedbackAttachmentUpload";
import type { CreateReportInput } from "@/hooks/useFeedbackReports";
import type { FeedbackKind } from "@/types";

const MAX_ATTACHMENTS = 5;
/** Must match settings.feedback_video_max_count. Three recordings on one report
 *  is a conversation, not a report. */
const MAX_VIDEOS = 2;
const ACCEPTED_TYPES = "image/png,image/jpeg,image/webp,video/mp4,video/webm";
const APP_VERSION = import.meta.env.VITE_APP_VERSION ?? "dev";

interface NewReportDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onCreate: (
    input: CreateReportInput,
  ) => Promise<{ success: boolean; message?: string | null } | null | undefined>;
}

export function NewReportDialog({
  open,
  onOpenChange,
  onCreate,
}: NewReportDialogProps) {
  const [kind, setKind] = useState<FeedbackKind>("BUG");
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [shots, setShots] = useState<UploadedAttachment[]>([]);
  const [submitting, setSubmitting] = useState(false);
  const [dragging, setDragging] = useState(false);
  const fileInput = useRef<HTMLInputElement>(null);

  const { uploading, upload } = useFeedbackAttachmentUpload();

  const reset = useCallback(() => {
    // previewUrl is null for a video with no poster frame — nothing to revoke.
    shots.forEach((shot) => {
      if (shot.previewUrl) URL.revokeObjectURL(shot.previewUrl);
    });
    setKind("BUG");
    setTitle("");
    setDescription("");
    setShots([]);
  }, [shots]);

  const addFiles = useCallback(
    async (files: File[]) => {
      // `shots` is captured from this render and uploads are awaited one at a
      // time, so state never catches up between iterations. Count locally.
      let total = shots.length;
      let videos = shots.filter((shot) => shot.mediaType === "VIDEO").length;

      for (const file of files) {
        if (total >= MAX_ATTACHMENTS) {
          toast.error(`Enintään ${MAX_ATTACHMENTS} liitettä yhdessä ilmoituksessa`);
          return;
        }
        if (file.type.startsWith("video/") && videos >= MAX_VIDEOS) {
          toast.error(`Enintään ${MAX_VIDEOS} videota yhdessä ilmoituksessa`);
          return;
        }
        const uploaded = await upload(file);
        if (!uploaded) continue;
        total += 1;
        if (uploaded.mediaType === "VIDEO") videos += 1;
        setShots((current) => [...current, uploaded]);
      }
    },
    [shots, upload],
  );

  /**
   * Clipboard paste, anywhere in the dialog including the description field.
   *
   * Accepts video as well as images: a user who copies an .mp4 in Explorer gets a
   * file reference on the clipboard and pasting it should work. No recorder puts
   * video bytes there directly, which is why the drop zone advertises drag-drop
   * for clips instead.
   */
  useEffect(() => {
    if (!open) return;
    const onPaste = (event: ClipboardEvent) => {
      const files = Array.from(event.clipboardData?.items ?? [])
        .filter(
          (item) =>
            item.kind === "file" &&
            (item.type.startsWith("image/") || item.type.startsWith("video/")),
        )
        .map((item) => item.getAsFile())
        .filter((file): file is File => file !== null);
      if (files.length === 0) return;
      event.preventDefault();
      void addFiles(files);
    };
    document.addEventListener("paste", onPaste);
    return () => document.removeEventListener("paste", onPaste);
  }, [open, addFiles]);

  const submit = async () => {
    if (title.trim().length < 3) {
      toast.error("Anna ilmoitukselle otsikko");
      return;
    }
    setSubmitting(true);
    try {
      const result = await onCreate({
        kind,
        title: title.trim(),
        description: description.trim() || null,
        context: {
          path: window.location.pathname + window.location.search,
          browser: navigator.userAgent,
          os: navigator.platform,
          appVersion: APP_VERSION,
        },
        attachments: shots.map((shot) => ({
          id: shot.id,
          caption: shot.caption.trim() || null,
        })),
      });
      if (result?.success) {
        toast.success(result.message ?? "Ilmoitus lähetetty");
        reset();
        onOpenChange(false);
      } else {
        toast.error(result?.message ?? "Ilmoituksen lähetys epäonnistui");
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (!next) reset();
        onOpenChange(next);
      }}
    >
      <DialogContent className="max-h-[88vh] max-w-2xl overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Uusi ilmoitus</DialogTitle>
          <DialogDescription>
            Kerro mitä tapahtui ja liitä kuvakaappaus. Kuva riittää usein
            selityksen sijaan.
          </DialogDescription>
        </DialogHeader>

        {/* pt-5: DialogHeader bleeds to the dialog edges, so without it "Tyyppi"
            sits flush against the header's bottom border. */}
        <div className="grid gap-4 pt-5">
          <div className="grid gap-2">
            <Label>Tyyppi</Label>
            <div role="group" aria-label="Ilmoituksen tyyppi" className="flex gap-2">
              {(["BUG", "IDEA"] as FeedbackKind[]).map((value) => {
                const meta = FEEDBACK_KIND_META[value];
                const Icon = meta.icon;
                return (
                  <button
                    key={value}
                    type="button"
                    aria-pressed={kind === value}
                    onClick={() => setKind(value)}
                    className={cn(
                      "flex flex-1 items-center gap-2.5 rounded-lg border p-3 text-left text-sm font-semibold",
                      kind === value
                        ? "border-primary bg-primary/10"
                        : "text-muted-foreground",
                    )}
                  >
                    <Icon className={cn("h-4 w-4 shrink-0", meta.iconClass)} />
                    <span>
                      {meta.label}
                      <small className="block text-xs font-normal text-muted-foreground">
                        {meta.hint}
                      </small>
                    </span>
                  </button>
                );
              })}
            </div>
          </div>

          <div className="grid gap-2">
            <Label htmlFor="feedback-title">Otsikko</Label>
            <Input
              id="feedback-title"
              value={title}
              onChange={(event) => setTitle(event.target.value)}
              maxLength={200}
              placeholder="Esim. Tehtävän määräaika siirtyy päivän taaksepäin"
            />
          </div>

          <div className="grid gap-2">
            <Label htmlFor="feedback-description">
              Ilmoituksen kuvaus{" "}
              <span className="font-normal text-muted-foreground">
                — mitä teit, mitä odotit, mitä tapahtui
              </span>
            </Label>
            <Textarea
              id="feedback-description"
              value={description}
              onChange={(event) => setDescription(event.target.value)}
              rows={5}
              placeholder="Kirjoita tai liitä kuvakaappaus suoraan tähän kenttään."
            />
          </div>

          <div className="grid gap-2">
            <Label>
              Kuvakaappaukset ja videot{" "}
              <span className="font-normal text-muted-foreground">
                — enintään {MAX_ATTACHMENTS} liitettä. Jokaiselle liittelle tulee oma kuvateksti.
              </span>
            </Label>

            {shots.length < MAX_ATTACHMENTS && (
              <button
                type="button"
                onClick={() => fileInput.current?.click()}
                onDragEnter={(event) => {
                  event.preventDefault();
                  setDragging(true);
                }}
                onDragOver={(event) => event.preventDefault()}
                onDragLeave={() => setDragging(false)}
                onDrop={(event) => {
                  event.preventDefault();
                  setDragging(false);
                  void addFiles(Array.from(event.dataTransfer.files));
                }}
                className={cn(
                  "flex flex-col items-center gap-1.5 rounded-lg border border-dashed bg-muted/40 px-4 py-6 text-center",
                  dragging && "border-primary bg-primary/10",
                )}
              >
                <ImageIcon className="h-6 w-6 text-muted-foreground" />
                <span className="text-sm font-semibold">
                  Liitä kuvakaappaus Ctrl + V, tai valitse tiedosto
                </span>
                {/* Video cannot be pasted from a recorder, so the route that
                    does work is named explicitly rather than left implied. */}
                <span className="text-xs text-muted-foreground">
                  Ruutuvideo: PrintScreen + video ikoni
                </span>
              </button>
            )}
            <input
              ref={fileInput}
              type="file"
              accept={ACCEPTED_TYPES}
              multiple
              hidden
              onChange={(event) => {
                void addFiles(Array.from(event.target.files ?? []));
                event.target.value = "";
              }}
            />

            <AttachmentTray
              items={shots}
              onCaptionChange={(id, caption) =>
                setShots((current) =>
                  current.map((shot) =>
                    shot.id === id ? { ...shot, caption } : shot,
                  ),
                )
              }
              onRemove={(id) =>
                setShots((current) => {
                  const removed = current.find((shot) => shot.id === id);
                  // previewUrl is null for a video with no poster frame.
                  if (removed?.previewUrl) URL.revokeObjectURL(removed.previewUrl);
                  return current.filter((shot) => shot.id !== id);
                })
              }
            />
          </div>
        </div>

        <DialogFooter className="items-center gap-2 sm:justify-start">
          <Button onClick={submit} disabled={submitting || uploading}>
            {submitting ? "Lähetetään…" : "Lähetä ilmoitus"}
          </Button>
          <Button variant="ghost" onClick={() => onOpenChange(false)}>
            Peruuta
          </Button>
          <span className="text-xs text-muted-foreground sm:ml-auto">
            Ilmoitus näkyy kaikille sisäisille käyttäjille
          </span>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
